import React from 'react';
import { ChoiceQuestion, MultipleChoiceQuestion, Question, Quiz } from '../../../common';
import update from 'immutability-helper';
import Button from '@/components/input/Button';
import { Add } from '@mui/icons-material';
import Option from './Options';
import theme from '@/constants/theme';

interface Props {
  index: number;
  type: ChoiceQuestion['type'];
  questionId: ChoiceQuestion['id'];
  options: ChoiceQuestion['options'];
  config: ChoiceQuestion['config'];
  setQuiz: React.Dispatch<React.SetStateAction<Quiz | null>>;
}

const QuestionOptions: React.FC<Props> = ({ questionId, type, options, setQuiz, index, config }) => {
  const handleChangeOptions = (optionIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuiz((prevQuiz) =>
      update(prevQuiz, {
        questions: {
          [index]: {
            options: {
              [optionIndex]: { $set: e.target.value },
            },
          },
        },
      }),
    );
  };

  const removeOption = (optIndex: number) => {
    setQuiz((prevQuiz) =>
      update(prevQuiz, {
        questions: {
          [index]: {
            options: {
              $splice: [[optIndex, 1]],
            },
          },
        },
      }),
    );
  };

  const addOption = () => {
    setQuiz((prevQuiz) =>
      update(prevQuiz, {
        questions: {
          [index]: {
            options: {
              $push: [''],
            },
          },
        },
      }),
    );
  };

  const moveOption = (fromIndex: number, toIndex: number) => {
    setQuiz((prevQuiz) => {
      if (!prevQuiz) return prevQuiz;
      const value = (((prevQuiz ?? { questions: [] }).questions[index]
        .options as ChoiceQuestion['options']) ?? [])[fromIndex];
      return update(prevQuiz, {
        questions: {
          [index]: {
            options: {
              $splice: [
                [fromIndex, 1],
                [toIndex, 0, value],
              ],
            },
          },
        },
      });
    });
  };

  const handleChangeConfig = (field: keyof MultipleChoiceQuestion['config'], value: boolean) => {
    setQuiz((prev) =>
      update(prev, {
        questions: {
          [index]: {
            config: {
              $set: { [field]: value },
            },
          },
        },
      }),
    );
  };

  return (
    <>
      {options.map((option, optIndex) => (
        <Option
          key={optIndex}
          option={option}
          optIndex={optIndex}
          index={optIndex}
          moveOption={moveOption}
          handleChangeOptions={handleChangeOptions}
          removeOption={removeOption}
          questionId={questionId}
        />
      ))}
      {(['multipleChoice', 'singleChoice'] as Question['type'][]).includes(type) && (
        <div style={{ display: 'flex', gap: 8, flexDirection: 'row', justifyContent: 'space-between' }}>
          <Button type="button" variant="ghost" size="small" Icon={Add} onClick={addOption}>
            Añadir opción
          </Button>
          {(['multipleChoice'] as Question['type'][]).includes(type) && (
            <label
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                color: theme.colors.text.body,
                accentColor: theme.colors.primary,
              }}
            >
              <input
                type="checkbox"
                checked={(config as MultipleChoiceQuestion['config'])?.countSelected ?? false}
                onChange={(e) => handleChangeConfig('countSelected', Boolean(e.target.checked))}
              />
              <span>Mostrar la cantidad de opciones seleccionadas</span>
            </label>
          )}
        </div>
      )}
    </>
  );
};

export default QuestionOptions;
